import React from 'react';
import { Container, Title, Text, Button, Stack, Paper, Code, Box } from '@mantine/core';
import { IconAlertTriangle, IconHome, IconRefresh } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { ErrorFallbackProps } from './ErrorBoundary';

/**
 * ErrorFallback Component
 *
 * Beautiful error UI displayed when ErrorBoundary catches an error.
 * Provides user-friendly messaging and recovery options.
 *
 * Features:
 * - User-friendly error message
 * - Error details in development mode only
 * - "Try Again" button to reset error boundary
 * - "Go Home" button to navigate to dashboard
 * - Responsive, accessible design with Mantine components
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={ErrorFallback}>
 *   <SomeComponent />
 * </ErrorBoundary>
 * ```
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
  const navigate = useNavigate();

  const handleGoHome = (): void => {
    resetError();
    navigate('/');
  };

  const isDevelopment = import.meta.env.DEV;

  return (
    <Container size="sm" py="xl" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper shadow="md" p="xl" radius="md" withBorder w="100%">
        <Stack align="center" gap="lg">
          {/* Icon */}
          <Box
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-red-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
          </Box>

          {/* Title */}
          <Title order={2} ta="center">
            Something went wrong
          </Title>

          {/* Description */}
          <Text ta="center" c="dimmed" size="lg">
            We're sorry, but something unexpected happened. This error has been logged and we'll look into it.
          </Text>

          {/* Error details (development only) */}
          {isDevelopment && (
            <Stack gap="xs" w="100%">
              <Text size="sm" fw={600} c="red">
                Error Details (Development Only):
              </Text>
              <Code block c="red">
                {error.message}
              </Code>
              {error.stack && (
                <Code block style={{ fontSize: '0.75rem', maxHeight: '200px', overflow: 'auto' }}>
                  {error.stack}
                </Code>
              )}
            </Stack>
          )}

          {/* Action buttons */}
          <Stack gap="sm" w="100%" maw={300}>
            <Button
              fullWidth
              size="md"
              leftSection={<IconRefresh size={18} />}
              onClick={resetError}
              variant="filled"
            >
              Try Again
            </Button>
            <Button
              fullWidth
              size="md"
              leftSection={<IconHome size={18} />}
              onClick={handleGoHome}
              variant="light"
            >
              Go to Dashboard
            </Button>
          </Stack>

          {/* Additional help text */}
          <Text size="sm" ta="center" c="dimmed">
            If this problem persists, please contact support.
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
};
